import { useQueryClient } from "@tanstack/react-query";
import { XIcon } from "lucide-react";
import { Autocomplete, useFilter } from "react-aria-components";
import { Controller } from "react-hook-form";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Input } from "@/components/field";
import { ChevronDownIcon } from "@/components/icons.tsx";
import { SearchField } from "@/components/search-field";
import {
  Select,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { Text } from "@/components/text";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useGetBrands } from "@/features/request/edit/hooks/useGetBrands";
import { creatorOptionsKeys } from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";

export function SelectBrand() {
  const methods = useEditRequestFormContext();
  const { data: request } = useGetRequest();
  const { data: brands } = useGetBrands();
  const { updateField } = useFieldUpdate();
  const queryClient = useQueryClient();
  const { contains } = useFilter({ sensitivity: "base" });

  const handleUpdate = (
    brandId: null | number,
    onChange: (...event: unknown[]) => void,
  ) => {
    onChange(brandId);
    void updateField("brandId", brandId, () => {
      void queryClient.invalidateQueries({ queryKey: ["request"] });
      void queryClient.invalidateQueries({
        queryKey: creatorOptionsKeys.byRequestId(request?.id),
      });
    });
  };

  return (
    <Controller
      control={methods.control}
      name="brandId"
      render={({ field: { name, onChange, value } }) => (
        <Select
          aria-label="Select a brand"
          name={name}
          onSelectionChange={(selectedValue) =>
            handleUpdate(Number(selectedValue), onChange)
          }
          selectedKey={value}
        >
          <Button
            className="flex items-center justify-center outline-none whitespace-nowrap"
            variant="unstyled"
          >
            <Badge size="small" variant="primary">
              {brands?.find((option) => option.id === value)?.name ??
                "Select a brand"}
              <Badge.Icon>
                <ChevronDownIcon />
              </Badge.Icon>
            </Badge>
          </Button>
          <SelectPopover
            className="flex w-fit flex-col overflow-hidden"
            placement="bottom start"
          >
            <Autocomplete filter={contains}>
              <SearchField
                aria-label="Search brands"
                autoFocus
                className="shrink-0 px-sm pt-xs"
              >
                <Input placeholder="Search brands..." />
              </SearchField>

              <SelectListBox
                className="min-h-0"
                renderEmptyState={() => (
                  <Text className="p-sm text-center text-text-tertiary">
                    No brands found
                  </Text>
                )}
              >
                {brands?.map(({ id, name }) => (
                  <SelectListItem
                    id={id}
                    key={id}
                    renderTrailing={(isSelected) =>
                      isSelected ? (
                        <span
                          aria-label="Clear brand"
                          className="flex shrink-0 cursor-pointer items-center text-fg-quaternary transition hover:text-text-primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleUpdate(null, onChange);
                          }}
                          onPointerDown={(event) => event.stopPropagation()}
                          role="button"
                          tabIndex={-1}
                        >
                          <XIcon className="size-4" />
                        </span>
                      ) : null
                    }
                    textValue={name}
                  >
                    {name}
                  </SelectListItem>
                ))}
              </SelectListBox>
            </Autocomplete>
          </SelectPopover>
        </Select>
      )}
    />
  );
}
